import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import {
  listSuperAppConfigs,
  createSuperAppConfig,
  deleteSuperAppConfig,
} from "../lib/superappConfig";
import {
  FaPlus,
  FaSearch,
  FaRocket,
  FaPalette,
  FaLayerGroup,
  FaTrashAlt,
} from "react-icons/fa";
import { Modal, Button, Form } from "react-bootstrap";

import { THEME_PRESETS } from "../ThemeContext";

export default function AppSelector() {
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const history = useHistory();

  // Creation Wizard State
  const [newAppName, setNewAppName] = useState("");
  const [newAppId, setNewAppId] = useState("");
  const [newAppTheme, setNewAppTheme] = useState("purple");
  const [creating, setCreating] = useState(false);

  // Vik (Jan 22, 2026): Delete confirmation state - require explicit user confirmation before deletion
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [appToDelete, setAppToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    loadApps();
  }, []);

  async function loadApps() {
    try {
      setLoading(true);
      const list = await listSuperAppConfigs();
      // Filter out non-app configs if necessary, or just show all
      setApps(list);
    } catch (e) {
      console.error("Failed to load apps", e);
    } finally {
      setLoading(false);
    }
  }

  const handleCreateApp = async (e) => {
    e.preventDefault();
    if (!newAppName || !newAppId) return;

    setCreating(true);
    try {
      const selectedTheme =
        THEME_PRESETS.find((t) => t.id === newAppTheme) || THEME_PRESETS[0];

      // Basic initial config template
      const initialConfig = {
        header: {
          title: newAppName,
          brand: { logoUrl: "/images/logo.png" },
          primaryMenu: { items: [] },
        },
        features: {
          showLogin: true,
          showTryForFree: false,
        },
        screens: {
          home: {
            type: "hero",
            title: `Welcome to ${newAppName}`,
            subtitle: "This is your new app. Start building!",
            cta: { label: "Get Started", href: "/docs" },
          },
        },
        theme: selectedTheme,
      };

      await createSuperAppConfig(newAppId, initialConfig);

      // Redirect to the new app
      window.location.href = `/?appId=${encodeURIComponent(newAppId)}`;
    } catch (e) {
      console.error("Failed to create app", e);
      alert("Failed to create app: " + e.message);
    } finally {
      setCreating(false);
    }
  };

  // Vik (Jan 22, 2026): Handle delete click - opens confirmation modal
  const handleDeleteClick = (e, app) => {
    e.stopPropagation(); // Prevent card click from navigating
    const id = app.id || app._id;
    const config = app.config || {};
    const title = config.header?.title || id;
    setAppToDelete({ id, title });
    setShowDeleteModal(true);
  };

  // Vik (Jan 22, 2026): Confirm deletion - calls API and updates UI state
  const handleConfirmDelete = async () => {
    if (!appToDelete) return;

    setDeleting(true);
    try {
      await deleteSuperAppConfig(appToDelete.id);
      // Vik (Jan 22, 2026): Update local state to reflect deletion without page reload
      setApps((prev) => prev.filter((a) => (a.id || a._id) !== appToDelete.id));
      setShowDeleteModal(false);
      setAppToDelete(null);
    } catch (e) {
      console.error("Failed to delete app", e);
      alert("Failed to delete app: " + e.message);
    } finally {
      setDeleting(false);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setAppToDelete(null);
  };

  const filteredApps = apps.filter((app) => {
    const id = app.id || app._id || "";
    // Some configs might be wrapped or have different structures
    const name = app.config?.header?.title || id;
    return (
      name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      id.toLowerCase().includes(searchTerm.toLowerCase())
    );
  });

  return (
    <div className="app-selector-container">
      <div className="app-selector-header">
        <div className="custom-container">
          <h1 className="gradient-text">Welcome to SuperApp</h1>
          <p className="subtitle">
            Select an app to launch or create a new one.
          </p>
        </div>
      </div>

      <div className="custom-container">
        <div className="app-toolbar">
          <div className="search-bar">
            <FaSearch className="search-icon" />
            <input
              type="text"
              placeholder="Search apps..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <button
            className="primary-gradient-btn create-btn"
            onClick={() => setShowCreateModal(true)}
          >
            <FaPlus /> Create New App
          </button>
        </div>

        {loading ? (
          <div className="loading-state">
            <div className="spinner"></div>
            <p>Loading your workspace...</p>
          </div>
        ) : (
          <div className="apps-grid">
            {/* Create New Card (First item) */}
            <div
              className="app-card create-card"
              onClick={() => setShowCreateModal(true)}
            >
              <div className="card-icon">
                <FaPlus />
              </div>
              <h3>Create New App</h3>
              <p>Start from scratch with a new workspace</p>
            </div>

            {/* App List */}
            {filteredApps.map((app, idx) => {
              const id = app.id || app._id || `app-${idx}`;
              const config = app.config || {};
              const title = config.header?.title || id;
              const description =
                config.header?.brand?.description || "No description";

              return (
                <div
                  key={id}
                  className="app-card"
                  onClick={() =>
                    (window.location.href = `/?appId=${encodeURIComponent(id)}`)
                  }
                >
                  <div className="app-preview">
                    {/* Placeholder for app preview/screenshot */}
                    <div
                      className="preview-placeholder"
                      style={{ background: `hsl(${idx * 60}, 70%, 80%)` }}
                    >
                      <span className="app-initial">
                        {title.charAt(0).toUpperCase()}
                      </span>
                    </div>
                  </div>
                  <div className="app-info">
                    <h3>{title}</h3>
                    <p className="app-id">ID: {id}</p>
                  </div>
                  {/* Vik (Jan 22, 2026): App action buttons - delete and launch */}
                  <div className="app-actions">
                    <button
                      className="delete-btn"
                      onClick={(e) => handleDeleteClick(e, app)}
                      title="Delete app"
                    >
                      <FaTrashAlt />
                    </button>
                    <button className="launch-btn">
                      Launch <FaRocket />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Create App Modal */}
      <Modal
        show={showCreateModal}
        onHide={() => setShowCreateModal(false)}
        centered
        className="premium-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>Create New App</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleCreateApp}>
            <Form.Group className="mb-3">
              <Form.Label>App Name</Form.Label>
              <Form.Control
                type="text"
                placeholder="e.g. Employee Portal"
                value={newAppName}
                onChange={(e) => {
                  setNewAppName(e.target.value);
                  // Auto-generate ID from name
                  if (!newAppId) {
                    setNewAppId(
                      e.target.value.toLowerCase().replace(/\s+/g, "-"),
                    );
                  }
                }}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>App ID (Unique)</Form.Label>
              <Form.Control
                type="text"
                placeholder="e.g. employee-portal-v1"
                value={newAppId}
                onChange={(e) => setNewAppId(e.target.value)}
                required
              />
              <Form.Text className="text-muted">
                This will be used in the URL: ?appId=...
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Theme</Form.Label>
              <div className="theme-selector">
                {THEME_PRESETS.map((theme) => (
                  <div
                    key={theme.id}
                    className={`theme-option ${
                      newAppTheme === theme.id ? "selected" : ""
                    }`}
                    onClick={() => setNewAppTheme(theme.id)}
                  >
                    <div
                      className="theme-preview"
                      style={{
                        background: `linear-gradient(135deg, ${theme.colors.primary} 0%, ${theme.colors.secondary} 100%)`,
                      }}
                    ></div>
                    <span>{theme.name}</span>
                  </div>
                ))}
              </div>
            </Form.Group>

            <div className="d-flex justify-content-end mt-4">
              <Button
                variant="secondary"
                onClick={() => setShowCreateModal(false)}
                style={{ marginRight: "16px" }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="primary-gradient-btn"
                disabled={creating}
              >
                {creating ? "Creating..." : "Create App"}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      {/* Vik (Jan 22, 2026): Delete confirmation modal - prevents accidental deletions */}
      <Modal
        show={showDeleteModal}
        onHide={handleCancelDelete}
        centered
        className="premium-modal delete-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>Delete App</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="delete-confirmation">
            <div className="delete-icon-wrapper">
              <FaTrashAlt className="delete-warning-icon" />
            </div>
            <h4>Are you sure?</h4>
            <p>
              You are about to delete <strong>"{appToDelete?.title}"</strong>.
              <br />
              This action cannot be undone.
            </p>
            <div className="delete-app-id">
              App ID: <code>{appToDelete?.id}</code>
            </div>
          </div>
          <div className="d-flex justify-content-end mt-4">
            <Button
              variant="secondary"
              onClick={handleCancelDelete}
              style={{ marginRight: "16px" }}
              disabled={deleting}
            >
              Cancel
            </Button>
            <Button
              className="delete-confirm-btn"
              onClick={handleConfirmDelete}
              disabled={deleting}
            >
              {deleting ? "Deleting..." : "Delete App"}
            </Button>
          </div>
        </Modal.Body>
      </Modal>

      <style>{`
        .app-selector-container {
          min-height: 100vh;
          background: #0b1221; /* Dark background */
          font-family: 'Inter', sans-serif;
          color: #fff;
        }
        .app-selector-header {
          background: transparent;
          padding: 60px 0 40px;
          text-align: center;
          border-bottom: 1px solid rgba(255,255,255,0.1);
          margin-bottom: 40px;
        }
        .gradient-text {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          font-weight: 800;
          font-size: 3rem;
          margin-bottom: 16px;
        }
        .subtitle {
          color: rgba(255,255,255,0.7);
          font-size: 1.2rem;
        }
        .app-toolbar {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 32px;
        }
        .search-bar {
          position: relative;
          width: 300px;
        }
        .search-icon {
          position: absolute;
          left: 12px;
          top: 50%;
          transform: translateY(-50%);
          color: #999;
        }
        .search-bar input {
          width: 100%;
          padding: 10px 10px 10px 36px;
          border: 1px solid rgba(255,255,255,0.2);
          background: rgba(255,255,255,0.05);
          color: #fff;
          border-radius: 8px;
          font-size: 14px;
          transition: all 0.2s;
        }
        .search-bar input:focus {
          border-color: #764ba2;
          outline: none;
          box-shadow: 0 0 0 3px rgba(118, 75, 162, 0.1);
          background: rgba(255,255,255,0.1);
        }
        .apps-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
          gap: 24px;
          padding-bottom: 60px;
        }
        .app-card {
          background: #1a2035; /* Card background */
          border-radius: 16px;
          overflow: hidden;
          box-shadow: 0 4px 12px rgba(0,0,0,0.2);
          transition: all 0.3s ease;
          cursor: pointer;
          border: 1px solid rgba(255,255,255,0.05);
          display: flex;
          flex-direction: column;
        }
        .app-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 12px 24px rgba(0,0,0,0.3);
          border-color: #764ba2;
        }
        .create-card {
          border: 2px dashed rgba(255,255,255,0.2);
          background: transparent;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          text-align: center;
          min-height: 280px;
          color: rgba(255,255,255,0.6);
        }
        .create-card:hover {
          border-color: #764ba2;
          color: #764ba2;
          background: rgba(118, 75, 162, 0.1);
        }
        .create-card .card-icon {
          font-size: 40px;
          margin-bottom: 16px;
          opacity: 0.5;
        }
        .app-preview {
          height: 140px;
          background: #252b42;
          position: relative;
          overflow: hidden;
        }
        .preview-placeholder {
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .app-initial {
          font-size: 48px;
          font-weight: 800;
          color: rgba(255,255,255,0.9);
          text-shadow: 0 2px 10px rgba(0,0,0,0.2);
        }
        .app-info {
          padding: 20px;
          flex-grow: 1;
        }
        .app-info h3 {
          font-size: 18px;
          font-weight: 700;
          margin: 0 0 4px;
          color: #fff;
        }
        .app-id {
          font-size: 12px;
          color: rgba(255,255,255,0.5);
          margin: 0;
          font-family: monospace;
        }
        .app-actions {
          padding: 16px 20px;
          border-top: 1px solid rgba(255,255,255,0.05);
          display: flex;
          justify-content: flex-end;
        }
        .launch-btn {
          background: transparent;
          border: none;
          color: #a0aec0;
          font-weight: 600;
          font-size: 14px;
          display: flex;
          align-items: center;
          gap: 6px;
          transition: color 0.2s;
        }
        .launch-btn:hover {
          color: #764ba2;
          text-decoration: none;
        }

        /* Premium Modal Styling */
        .premium-modal .modal-content {
          background: #1a2035; /* Darker, richer background */
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 20px;
          box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5);
          color: #fff;
          overflow: hidden;
        }
        
        .premium-modal .modal-header {
          border-bottom: 1px solid rgba(255, 255, 255, 0.05);
          padding: 24px 32px;
          background: rgba(0, 0, 0, 0.2);
        }
        
        .premium-modal .modal-title {
          font-weight: 700;
          font-size: 1.5rem;
          background: linear-gradient(135deg, #fff 0%, #a5b4fc 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
        }
        
        .premium-modal .btn-close {
          filter: invert(1) grayscale(100%) brightness(200%);
        }
        
        .premium-modal .modal-body {
          padding: 32px;
        }
        
        .premium-modal .form-label {
          color: #a5b4fc;
          font-weight: 500;
          font-size: 0.9rem;
          margin-bottom: 8px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }
        
        .premium-modal .form-control {
          background: rgba(0, 0, 0, 0.3);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 12px;
          color: #fff;
          padding: 14px 16px;
          font-size: 1rem;
          transition: all 0.3s ease;
        }
        
        .premium-modal .form-control:focus {
          background: rgba(0, 0, 0, 0.5);
          border-color: #667eea;
          box-shadow: 0 0 0 4px rgba(102, 126, 234, 0.15);
        }
        
        .premium-modal .form-text {
          color: rgba(255, 255, 255, 0.4) !important;
          font-size: 0.85rem;
          margin-top: 8px;
        }
        
        /* Theme Selector Styling */
        .theme-selector {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(100px, 1fr));
          gap: 16px;
          margin-top: 12px;
        }
        
        .theme-option {
          background: rgba(255, 255, 255, 0.03);
          border: 1px solid rgba(255, 255, 255, 0.05);
          border-radius: 16px;
          padding: 12px;
          cursor: pointer;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 10px;
        }
        
        .theme-option:hover {
          background: rgba(255, 255, 255, 0.08);
          transform: translateY(-2px);
        }
        
        .theme-option.selected {
          background: rgba(102, 126, 234, 0.1);
          border-color: #667eea;
          box-shadow: 0 0 0 2px rgba(102, 126, 234, 0.3);
        }
        
        .theme-preview {
          width: 48px;
          height: 48px;
          border-radius: 50%;
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.2);
          position: relative;
        }
        
        .theme-option.selected .theme-preview::after {
          content: '';
          position: absolute;
          top: -4px;
          left: -4px;
          right: -4px;
          bottom: -4px;
          border-radius: 50%;
          border: 2px solid #fff;
          opacity: 0.5;
        }
        
        .theme-option span {
          font-size: 0.85rem;
          font-weight: 500;
          color: rgba(255, 255, 255, 0.8);
        }
        
        .theme-option.selected span {
          color: #fff;
          font-weight: 600;
        }
        
        /* Modal Buttons */
        .premium-modal .btn-secondary {
          background: transparent;
          border: 1px solid rgba(255, 255, 255, 0.1);
          color: rgba(255, 255, 255, 0.7);
          padding: 10px 24px;
          border-radius: 10px;
          font-weight: 500;
          transition: all 0.2s ease;
          margin-right: 16px; /* Explicit spacing fallback */
        }
        
        .premium-modal .btn-secondary:hover {
          background: rgba(255, 255, 255, 0.05);
          color: #fff;
          border-color: rgba(255, 255, 255, 0.2);
        }
        
        .premium-modal .primary-gradient-btn {
          padding: 10px 32px;
          border-radius: 10px;
          font-weight: 600;
          letter-spacing: 0.5px;
          box-shadow: 0 4px 12px rgba(118, 75, 162, 0.3);
        }
        
        .premium-modal .primary-gradient-btn:hover {
          box-shadow: 0 6px 16px rgba(118, 75, 162, 0.5);
          transform: translateY(-1px);
        }

        /* Vik (Jan 22, 2026): Delete button and modal styles - consistent with existing UI theme */
        .delete-btn {
          background: transparent;
          border: none;
          color: rgba(255, 255, 255, 0.4);
          font-size: 14px;
          padding: 8px;
          border-radius: 8px;
          transition: all 0.2s ease;
          cursor: pointer;
          margin-right: 8px;
        }
        .delete-btn:hover {
          color: #ef4444;
          background: rgba(239, 68, 68, 0.1);
        }

        /* Delete Modal Styling */
        .delete-confirmation {
          text-align: center;
          padding: 20px 0;
        }
        .delete-icon-wrapper {
          width: 80px;
          height: 80px;
          background: rgba(239, 68, 68, 0.1);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 24px;
        }
        .delete-warning-icon {
          font-size: 32px;
          color: #ef4444;
        }
        .delete-confirmation h4 {
          font-size: 1.5rem;
          font-weight: 700;
          color: #fff;
          margin-bottom: 12px;
        }
        .delete-confirmation p {
          color: rgba(255, 255, 255, 0.7);
          font-size: 1rem;
          line-height: 1.6;
          margin-bottom: 16px;
        }
        .delete-confirmation strong {
          color: #fff;
        }
        .delete-app-id {
          background: rgba(0, 0, 0, 0.3);
          padding: 8px 16px;
          border-radius: 8px;
          display: inline-block;
          font-size: 0.9rem;
          color: rgba(255, 255, 255, 0.6);
        }
        .delete-app-id code {
          color: #a5b4fc;
          font-family: 'Fira Code', 'Consolas', monospace;
        }
        .delete-confirm-btn {
          background: linear-gradient(135deg, #dc2626 0%, #ef4444 100%);
          border: none;
          color: #fff;
          padding: 10px 32px;
          border-radius: 10px;
          font-weight: 600;
          letter-spacing: 0.5px;
          box-shadow: 0 4px 12px rgba(239, 68, 68, 0.3);
          transition: all 0.2s ease;
        }
        .delete-confirm-btn:hover {
          box-shadow: 0 6px 16px rgba(239, 68, 68, 0.5);
          transform: translateY(-1px);
          background: linear-gradient(135deg, #b91c1c 0%, #dc2626 100%);
        }
        .delete-confirm-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
          transform: none;
        }
      `}</style>
    </div>
  );
}
